import React, { Fragment, Component, useState, useEffect } from 'react'
import { Router, Route } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles'
import styled from '@emotion/styled'
import Polyglot from 'node-polyglot'
import queryString from 'query-string'
import * as UserLocale from 'get-user-locale'
import { createBrowserHistory } from 'history'
import UncaughtApplicationError from './components/UncaughtApplicationError'
import { theme, Products, defaultProduct, companyWebsiteUrl } from '../constants'
import CreateUser from './createUser'
import AcceptInvitation from './acceptInvitation'
import NavBar from './components/NavBar'
import packageJson from '../../package.json'
import LanguageSelect from './components/LanguageSelect.js'
import * as Translations from '../translations'
import localStorageService from '../services/localStorage'

const history = createBrowserHistory()

class App extends Component {
  constructor(props) {
    super(props)

    const queryParams = queryString.parse(window.location.search)
    const languageInfo = getLanguageInfo(queryParams)
    const referral = getReferral(queryParams)
    const registrationToken = getRegistrationToken(queryParams)
    const { invitationToken, invitationFrom, invitationTo } = getInvitationToken(queryParams)

    const url = window.location.search
    const params = url.split('?')[1] || ''
    const urlParams = params.split('&').reduce((acc, v) => {
      const [key, value] = v.split('=')
      acc[key] = decodeURIComponent(value)

      return acc
    }, {})

    const isInvitation = window.location.pathname.includes('invitation')
    const product = getProduct()

    // The product must be present in the URL to register. If it isn't and it's not an invitation,
    // send the user to the company website so they can choose which software they want to.
    if (!product && !isInvitation) {
      window.location.replace(companyWebsiteUrl)
      this.state = { redirecting: true }
      return
    }

    // Legacy invitation links (/invitation without a product prefix) default to Nexus branding.
    const resolvedProduct = product || defaultProduct
    applyProductTheme(resolvedProduct)

    this.productTheme = createMuiTheme({
      ...theme,
      palette: {
        ...theme.palette,
        primary: {
          ...theme.palette.primary,
          light: resolvedProduct.primaryMain,
          main: resolvedProduct.primaryMain,
          dark: resolvedProduct.primaryMain
        }
      }
    })

    this.state = {
      language: languageInfo.locale,
      referral,
      registrationToken,
      invitationToken,
      invitationFrom,
      invitationTo,
      urlParams,
      product: resolvedProduct,
      uncaughtError: false
    }

    window.I18n = new Polyglot(languageInfo)

    // Remove any query parameter after reading them, keeping the product in the
    // path so the user is always aware which software they are signing up for.
    if (isInvitation) {
      history.replace(`/${resolvedProduct.id}/invitation`)
    } else {
      history.replace(`/${resolvedProduct.id}`)
    }
  }

  componentDidMount() {
    console.info(`(Accounts) ${packageJson.version}`)
  }

  componentDidCatch(error, info) {
    console.log('Uncaught Error: ', error)
    console.log('Error Info: ', info)
    this.setState({ uncaughtError: true })
  }

  onLanguageChange = locale => {
    // Use the translations of the selected language.
    const translation = Object.values(Translations).find(v => v.locale === locale)
    window.I18n = new Polyglot(translation)

    // Store the language locally and update the state.
    localStorageService.set('language', locale)
    this.setState({ language: locale })
  }

  render() {
    // While redirecting to the company website (missing product), render nothing.
    if (this.state.redirecting) {
      return null
    }

    const { product } = this.state
    const productTheme = this.productTheme

    return this.state.uncaughtError ? (
      <UncaughtApplicationError />
    ) : (
      <Router history={history}>
        <Fragment>
          <ToastContainerStyles>
            <ToastContainer
              position={toast.POSITION.BOTTOM_RIGHT}
              pauseOnHover={false}
              closeButton={false}
              autoClose={false}
              closeOnClick
              hideProgressBar
            />
            <DismissAll />
          </ToastContainerStyles>
          <MuiThemeProvider theme={productTheme}>
            <NavBar referral={this.state.referral} />
            <LanguageSelect
              language={this.state.language}
              onLanguageChange={this.onLanguageChange}
            />
            <Container>
              <AppPages>
                <Route
                  exact
                  path={'/:product/invitation'}
                  render={props => (
                    <AcceptInvitation
                      product={product}
                      token={this.state.invitationToken}
                      from={this.state.invitationFrom}
                    />
                  )}
                />
                <Route
                  exact
                  path={'/:product'}
                  render={props => (
                    <CreateUser
                      product={product}
                      language={this.state.language}
                      email={this.state.invitationTo}
                      referral={this.state.referral}
                      token={this.state.registrationToken}
                      returnUrl={this.state.urlParams.returnUrl}
                      invitationToken={this.state.invitationToken}
                      invitationFrom={this.state.invitationFrom}
                      invitationTo={this.state.invitationTo}
                    />
                  )}
                />
              </AppPages>
            </Container>
          </MuiThemeProvider>
        </Fragment>
      </Router>
    )
  }
}

const Container = styled.div`
  width: 100%;
  max-width: 900px;
  margin: auto;
  margin-top: 20px;
  margin-bottom: 30px;
  @media (max-width: 700px) {
    margin: 0;
    max-width: 100%;
  }
`

const AppPages = styled.section`
  position: relative;
  scrollbar-width: none;
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;

  @media (max-width: 700px) {
    border: 0;
  }
`
const ToastContainerStyles = styled.div`
  .dismiss-all {
    position: fixed;
    bottom: 10px;
    right: 21px;
    color: ${theme.palette.text.main};
    background-color: ${theme.palette.common.white};
    z-index: 99999;
    padding: 5px 15px;
    border-radius: 0;
    box-shadow:
      0px 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 4px 5px 0px rgba(0, 0, 0, 0.14),
      0px 1px 10px 0px rgba(0, 0, 0, 0.12);
    font-size: 14px;
    cursor: pointer;
    outline: none;
  }

  .Toastify__toast-container {
    width: unset;
    min-width: 320px;
    max-width: 380px;
    max-height: 100vh;
    padding-top: 35px;
    scrollbar-width: none;
    overflow-y: overlay;
    overflow-x: hidden;
    margin-bottom: 15px;

    &::-webkit-scrollbar: {
      width: 0;
    }

    .Toastify__toast {
      font-size: 14px;
      border-radius: 0;
      box-shadow:
        0px 2px 4px -1px rgba(0, 0, 0, 0.2),
        0px 4px 5px 0px rgba(0, 0, 0, 0.14),
        0px 1px 10px 0px rgba(0, 0, 0, 0.12);

      &.Toastify__toast--success {
        background: ${theme.palette.primary.main};
      }

      &.Toastify__toast--error {
        background: ${theme.palette.error.main};
      }

      &.Toastify__toast--info {
        background: ${theme.palette.common.blue};
      }
    }
  }
`

function DismissAll() {
  const [numberOfToasts, setNumberOfToasts] = useState(0)
  useEffect(() => {
    toast.onChange(numberOfToastDisplayed => {
      setNumberOfToasts(numberOfToastDisplayed)
    })
  }, [])

  return numberOfToasts > 1 ? (
    <div
      className="dismiss-all"
      role="button"
      onClick={() => {
        toast.dismiss()
        setNumberOfToasts(0)
      }}
      tabIndex={-1}
    >
      {window.I18n.t('common.clearAll')}
    </div>
  ) : null
}

function getProduct() {
  const segment = window.location.pathname.split('/').filter(Boolean)[0]
  return segment ? Products[segment.toLowerCase()] : undefined
}

function applyProductTheme(product) {
  const root = document.documentElement
  root.style.setProperty('--metrica-green', product.primaryMain)
  root.style.setProperty('--green-gradient', product.gradient)
}

function getLanguageInfo(queryParams) {
  // Read language from local-storage.
  let languageInfo
  const storedLocale = localStorageService.get('language')
  if (storedLocale) {
    languageInfo = Object.values(Translations).find(v => v.locale === storedLocale)
  }

  // If null, read language from navigator settings.
  if (!languageInfo) {
    const userLocales = UserLocale.getUserLocales()
    for (let i = 0; i < userLocales.length; i += 1) {
      languageInfo = Object.values(Translations).find(v => v.locale === userLocales[i])
      if (languageInfo) {
        break
      }
    }
  }

  // If null, read language from URL params.
  if (!languageInfo) {
    const locale = queryParams.lang || queryParams.language
    languageInfo = Object.values(Translations).find(v => v.locale === locale)

    // If there's no language info in local-storage, save it.
    if (languageInfo) {
      localStorageService.set('language', locale)
    }
  }

  // If null, use default language.
  if (!languageInfo) {
    languageInfo = Translations.English
  }

  return languageInfo
}

function getReferral(queryParams) {
  let referral = queryParams.referral
  if (referral) {
    sessionStorage.setItem('referral', referral)
    sessionStorage.setItem('referral-ttl', Date.now() + 1000 * 60 * 60 * 24 * 7)
  } else {
    const referralTtl = sessionStorage.getItem('referral-ttl')
    if (referralTtl && Date.now() > referralTtl) {
      sessionStorage.removeItem('referral')
      sessionStorage.removeItem('referral-ttl')
    } else {
      referral = sessionStorage.getItem('referral')
    }
  }

  return referral
}

function getRegistrationToken(queryParams) {
  let token = queryParams.token
  if (token) {
    sessionStorage.setItem('jwt', token)
    sessionStorage.setItem('jwt-ttl', Date.now() + 1000 * 60 * 60 * 24 * 7)
  } else {
    const jwtTtl = sessionStorage.getItem('jwt-ttl')
    if (jwtTtl && Date.now() > jwtTtl) {
      sessionStorage.removeItem('jwt')
      sessionStorage.removeItem('jwt-ttl')
    } else {
      token = sessionStorage.getItem('jwt')
    }
  }

  return token
}

function getInvitationToken(queryParams) {
  let invitationToken = queryParams.invitationToken
  let invitationFrom = decodeURIComponent(queryParams.invitationFrom) || 'unknown'
  let invitationTo = decodeURIComponent(queryParams.invitationTo) || ''

  if (invitationToken) {
    sessionStorage.setItem('invitation-token', invitationToken)
    sessionStorage.setItem('invitation-from', invitationFrom)
    sessionStorage.setItem('invitation-to', invitationTo)
    sessionStorage.setItem('invitation-token-ttl', Date.now() + 1000 * 60 * 60 * 24 * 7)
  } else {
    const invitationTokenTtl = sessionStorage.getItem('invitation-token-ttl')
    if (invitationTokenTtl && Date.now() > invitationTokenTtl) {
      sessionStorage.removeItem('invitation-token')
      sessionStorage.removeItem('invitation-from')
      sessionStorage.removeItem('invitation-to')
      sessionStorage.removeItem('invitation-token-ttl')
    } else {
      invitationToken = sessionStorage.getItem('invitation-token')
      invitationFrom = sessionStorage.getItem('invitation-from') || 'unknown'
      invitationTo = sessionStorage.getItem('invitation-to') || ''
    }
  }

  return { invitationToken, invitationFrom, invitationTo }
}

export default App
